import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  ClockIcon,
  CheckCircleIcon,
  StarIcon,
} from '@heroicons/react/24/outline';

const features = [
  {
    name: 'Opportunity Discovery',
    description: 'Find federal, state, and local contracts that match your capabilities with AI-powered matching.',
    icon: MagnifyingGlassIcon,
  },
  {
    name: 'GovCon AI Chat',
    description: 'Get instant answers about RFPs, compliance, and proposal strategy from our AI assistant.',
    icon: ChatBubbleLeftRightIcon,
  },
  {
    name: 'AI Proposal Writer',
    description: 'Generate compliant, compelling proposal sections in minutes, not days.',
    icon: DocumentTextIcon,
  },
  {
    name: 'Pipeline Management',
    description: 'Track opportunities from discovery to award with our GovCon-focused CRM.',
    icon: ChartBarIcon,
  },
  {
    name: 'Enterprise Security',
    description: 'FedRAMP and CMMC Level 2 compliance for enterprise security requirements.',
    icon: ShieldCheckIcon,
  },
  {
    name: 'Always Available',
    description: '24/7 access to your data and AI assistance. Never miss an opportunity.',
    icon: ClockIcon,
  },
];

const testimonials = [
  {
    content: "Clevor has transformed how we approach government contracting. We've increased our win rate by 40% in just 6 months.",
    author: 'Sarah Johnson',
    role: 'BD Director, TechSecure Solutions',
  },
  {
    content: 'The AI proposal writer alone has saved us hundreds of hours. The quality is outstanding and it understands GovCon requirements.',
    author: 'Michael Chen',
    role: 'CEO, GovInnovate',
  },
  {
    content: 'Finally, a tool built specifically for government contractors. The integrations with SAM.gov and GSA are game-changers.',
    author: 'Robert Williams',
    role: 'Capture Manager, FederalTech',
  },
];

const stats = [
  { label: 'Active Contractors', value: '500+' },
  { label: 'Contracts Won', value: '$250M+' },
  { label: 'Time Saved', value: '10,000+ hrs' },
  { label: 'Win Rate Improvement', value: '35%' },
];

const Home: React.FC = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative isolate overflow-hidden">
        <div className="absolute inset-0 gradient-bg" />
        <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mx-auto max-w-2xl text-center"
          >
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
              Win More Government Contracts with{' '}
              <span className="gradient-text">AI-Powered Intelligence</span>
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Discover opportunities, write winning proposals, and manage your GovCon pipeline—all in one platform. 
              Built for contractors who want to compete at the highest level.
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <Link to="/signup" className="btn-primary text-base px-8 py-3">
                Start Free Trial
              </Link>
              <Link to="/features" className="text-sm font-semibold leading-6 text-gray-900">
                Learn more <span aria-hidden="true">→</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-white py-12 border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <dl className="grid grid-cols-2 gap-8 text-center lg:grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.label} className="mx-auto flex max-w-xs flex-col gap-y-4">
                <dt className="text-base leading-7 text-gray-600">{stat.label}</dt>
                <dd className="order-first text-3xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
                  {stat.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-base font-semibold leading-7 text-brand-600">Everything you need</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              All-in-one platform for GovCon success
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              From opportunity discovery to proposal submission, Clevor streamlines every step of your government contracting workflow.
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-10 lg:max-w-none lg:grid-cols-3">
              {features.map((feature) => (
                <motion.div
                  key={feature.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="flex flex-col"
                >
                  <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                    <feature.icon className="h-5 w-5 flex-none text-brand-600" aria-hidden="true" />
                    {feature.name}
                  </dt>
                  <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">{feature.description}</p>
                  </dd>
                </motion.div>
              ))}
            </dl>
          </div>
        </div>
      </div>

      {/* How it Works */}
      <div className="bg-gray-50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              How Clevor Works
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Our streamlined process turns opportunities into wins
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-2xl lg:max-w-none">
            <div className="grid grid-cols-1 gap-y-8 lg:grid-cols-3 lg:gap-x-8">
              {[
                {
                  step: '01',
                  title: 'Discover',
                  description: 'AI scans thousands of opportunities and matches them to your capabilities.',
                },
                {
                  step: '02',
                  title: 'Analyze',
                  description: 'Get AI-powered analysis of requirements, risks, and competitiveness.',
                },
                {
                  step: '03',
                  title: 'Win',
                  description: 'Generate compliant proposals and track your pipeline to award.',
                },
              ].map((item) => (
                <div key={item.step} className="relative text-center lg:text-left">
                  <div className="text-6xl font-bold text-brand-200">{item.step}</div>
                  <h3 className="mt-4 text-xl font-semibold text-gray-900">{item.title}</h3>
                  <p className="mt-2 text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="text-center text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Trusted by 500+ Government Contractors
          </h2>
          <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex flex-col justify-between bg-white p-8 shadow-sm ring-1 ring-gray-200 rounded-2xl"
              >
                <div>
                  <div className="flex gap-x-1 text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <StarIcon key={i} className="h-5 w-5 fill-current" />
                    ))}
                  </div>
                  <p className="mt-6 text-lg leading-8 text-gray-600">"{testimonial.content}"</p>
                </div>
                <div className="mt-8 border-t border-gray-100 pt-6">
                  <p className="font-semibold text-gray-900">{testimonial.author}</p>
                  <p className="text-sm text-gray-600">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-brand-600">
        <div className="px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Ready to transform your GovCon business?
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-brand-100">
              Join 500+ contractors already using Clevor to win more government contracts.
              Start with a 7-day free trial.
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <Link
                to="/signup"
                className="rounded-md bg-white px-8 py-3 text-sm font-semibold text-brand-600 shadow-sm hover:bg-brand-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                Get Started Free
              </Link>
              <Link to="/pricing" className="text-sm font-semibold leading-6 text-white">
                View Pricing <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
